import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { ROUTES } from 'constants/routes';

const VIEW_TO_PAGE: Record<string, string> = {
  learn: 'jyotisha',
  syllabus: 'jyotish-marga',
};

const PAGE_TO_VIEW: Record<string, string> = {
  jyotisha: 'learn',
  'jyotish-marga': 'syllabus',
};

type ParashariLearnNavContextValue = {
  currentPage: string;
  setCurrentPage: (pageId: string) => void;
  isOnLearnRoute: boolean;
};

const ParashariLearnNavContext = createContext<ParashariLearnNavContextValue | null>(null);

function pageFromViewParam(view: string | null): string {
  if (view && VIEW_TO_PAGE[view]) return VIEW_TO_PAGE[view];
  return 'jyotisha';
}

export function ParashariLearnNavProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isOnLearnRoute = location.pathname.startsWith(ROUTES.PARASHARI_LEARN);

  const [currentPage, setCurrentPageState] = useState(() => {
    const statePage = (location.state as { initialPage?: string } | null)?.initialPage;
    if (statePage) return statePage;
    return pageFromViewParam(searchParams.get('view'));
  });

  useEffect(() => {
    if (!isOnLearnRoute) return;
    const statePage = (location.state as { initialPage?: string } | null)?.initialPage;
    if (statePage) {
      setCurrentPageState(statePage);
    }
  }, [isOnLearnRoute, location.state]);

  const setCurrentPage = useCallback(
    (pageId: string) => {
      setCurrentPageState(pageId);
      if (!isOnLearnRoute) return;
      const view = PAGE_TO_VIEW[pageId];
      if (!view) return;
      const next = new URLSearchParams(searchParams);
      if (view === 'learn') {
        next.delete('view');
      } else {
        next.set('view', view);
      }
      setSearchParams(next, { replace: true });
    },
    [isOnLearnRoute, searchParams, setSearchParams]
  );

  const value = useMemo(
    () => ({
      currentPage,
      setCurrentPage,
      isOnLearnRoute,
    }),
    [currentPage, setCurrentPage, isOnLearnRoute]
  );

  return (
    <ParashariLearnNavContext.Provider value={value}>{children}</ParashariLearnNavContext.Provider>
  );
}

export function useParashariLearnNav(): ParashariLearnNavContextValue {
  const ctx = useContext(ParashariLearnNavContext);
  if (!ctx) {
    throw new Error('useParashariLearnNav must be used within ParashariLearnNavProvider');
  }
  return ctx;
}

export function useParashariLearnNavOptional(): ParashariLearnNavContextValue | null {
  return useContext(ParashariLearnNavContext);
}
